import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createEvents, type CreateEventsResult } from '@api/events/mutations';
import { eventsQueryKeys } from '@api/events';
import { ordersQueryKeys } from '@api/orders/queries';
import { schedulerEntriesQueryKeys } from '@api/scheduler';
import { getFieldErrors } from '@api/client';
import { toast } from '@/hooks/use-toast';
import { useTranslation } from '../useTranslation';

interface UseCreateEventOptions {
  onSuccess?: (result: CreateEventsResult) => void;
}

export function useCreateEvent(options?: UseCreateEventOptions) {
  const queryClient = useQueryClient();
  const { onSuccess } = options || {};
  const { t } = useTranslation();

  return useMutation({
    mutationFn: createEvents,
    // createEvents never rejects — it settles every POST and returns
    // { created, errors } — so failures must be read here, not in onError
    // (GRAIN-2414: a failed create used to toast success and close the modal).
    onSuccess: (data, variables) => {
      if (data.created.length > 0) {
        queryClient.invalidateQueries({ queryKey: eventsQueryKeys.lists() });
        queryClient.invalidateQueries({ queryKey: eventsQueryKeys.orderEvents(variables.orderId) });
        queryClient.invalidateQueries({ queryKey: schedulerEntriesQueryKeys.forOrder(variables.orderId) });
        // The order payload carries derived event fields (draft_events,
        // has_publishable_events, fulfilment_events_count, documents_directly_generatable),
        // so refetch it too or the actions dropdown warns/gates on stale data — or takes
        // the direct-generation path after the count crosses the threshold (GRAIN-2469).
        queryClient.invalidateQueries({ queryKey: ordersQueryKeys.detail(variables.orderId) });
      }

      if (data.errors.length > 0) {
        const firstError = data.errors[0];
        const fieldErrors = getFieldErrors(firstError);
        const detail = fieldErrors ? Object.values(fieldErrors).join('; ') : firstError.message;

        toast({
          description: detail || t('create_event.create_error'),
          variant: 'destructive',
        });
        // Skip the close callback so the modal stays open with the user's input.
        return;
      }

      toast({
        description: t('create_event.create_success'),
      });

      onSuccess?.(data);
    },
    onError: (error) => {
      toast({
        description: error.message || t('create_event.create_error'),
        variant: 'destructive',
      });
    },
  });
}
